import { navigate } from 'gatsby';
import React, { FC } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import { DashboardCardLayout } from '@ui/atoms/DashboardCardLayout';
import { DashboardInfoDisplay } from '@ui/atoms/DashboardInfoDisplay';
import { DashboardInfoDisplayText } from '@ui/atoms/DashboardInfoDisplayText';
import { DashboardInfoSmallText } from '@ui/atoms/DashboardInfoSmallText';
import { EditButtonWrapper } from '@ui/atoms/EditButtonWrapper';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import FolxMembershipCard from '@ui/organisms/FolxMembershipCard';
import ProfileDropdown from '@ui/organisms/ProfileDropdown';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';

import spaced from '@ui/styled/Spaced';

import { IMemberDefaultPharmacy } from '@hooks/useMemberDefaultPharmacy';
import {
	B2BMember,
	MemberInfo,
	MembershipSubscription,
	SubscriptionInfo,
} from '@hooks/useMemberInfo';
import { IMemberInsurance } from '@hooks/useMemberInsurance';
import { IMemberInvoiceProposal } from '@hooks/useMemberInvoiceProposal';

import { maskInsurance } from '@helpers/Insurance/maskInsurance';

import { IAddressInfo } from '@folx-types/address';
import { IPaymentInfo } from '@folx-types/payment';

import theme from '@theme/FolxTheme';

export interface IBillingPageInformation {
	authenticatedMember: {
		legalFirstName: string;
		legalLastName: string;
		membershipSubscription: MembershipSubscription;
		invoices?: {
			nodes: Array<{
				created: string | null;
				total: number | null;
			}> | null;
		} | null;
		isMembershipWaivable: {
			isWaivable?: boolean | null;
		};
	} & B2BMember;
}

export interface IBillingPageContent {
	heading: string;
	preferredPharmacyText: string;
	paymentSectionHeading: string;
	paymentLabel: string;
	deliverySectionHeading: string;
	deliveryLabel: string;
	insuranceSectionHeading: string;
	insuranceLabel: string;
	shippingSectionHeading: string;
	shippingLabel: string;
	billsSectionHeading: string;
	billsLabel: string;
	winbackToast: string;
	cancelToast: string;
	folxMembership: {
		titleMonthly: string;
		titleYearly: string;
		eyebrowActiveYearly: string;
		eyebrowActive: string;
		badgeActive: string;
		ctaActive: string;
		eyebrowPastDue: string;
		badgePastDue: string;
		descriptionPastDue: string;
		textPastDue: string;
		ctaPastDue: string;
		eyebrowCanceled: string;
		badgeCanceled: string;
		descriptionCanceled: string;
		ctaCanceled: string;
		eyebrowB2b: string;
		descriptionB2b: string;
		textB2b: string;
		secondaryCtaPastDue: string;
		secondaryCtaSwitchToYearly: string;
		secondaryCtaSwitchToMonthly: string;
	};
}

interface IBillingProps {
	billingPageInformation?: IBillingPageInformation;
	pageContent: IBillingPageContent;
	member?: MemberInfo & SubscriptionInfo;
	deliveryMethod?: string;
	defaultPharmacy?: IMemberDefaultPharmacy;
	medicationBills?: IMemberInvoiceProposal[];
	memberInsurance?: IMemberInsurance[];
	address?: IAddressInfo;
	currentPaymentInfo?: IPaymentInfo;
	capsuleEligible: boolean;
	hasActiveSub: boolean;
	protectedNavigate?: ((intent?: string) => Promise<void>) | (() => void);
}

const pharmacyMap = {
	[medicationDeliveryConstants.CAPSULE_METHOD]: 'Capsule',
	[medicationDeliveryConstants.PHARMACY_METHOD]: 'Pharmacy',
	[medicationDeliveryConstants.FOLX_METHOD]: 'FOLX Shipping',
};

const SectionLabel = styled(BasicText)`
	margin-bottom: ${theme.spacing.sm};
`;

const DashboardInfoDisplayTextBold = styled(DashboardInfoDisplayText)`
	font-weight: 600;
`;

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);

const Billing: FC<IBillingProps> = ({
	billingPageInformation,
	member,
	address,
	currentPaymentInfo,
	deliveryMethod,
	defaultPharmacy,
	memberInsurance,
	pageContent,
	protectedNavigate = () => {
		return;
	},
}) => {
	const { folxMembership } = pageContent;
	const getFirstName = () => billingPageInformation?.authenticatedMember.legalFirstName;
	const getLastName = () => billingPageInformation?.authenticatedMember.legalLastName;
	const fullShippingName = `${getFirstName()} ${getLastName()}`;

	const isPreferredPharmacy = deliveryMethod === medicationDeliveryConstants.PHARMACY_METHOD;

	const Bills = () => (
		<DashboardInfoDisplay>
			<div>
				<DashboardInfoSmallText variant="field">{pageContent.billsLabel}</DashboardInfoSmallText>
			</div>
			<EditButtonWrapper>
				<Button disabled={false} label="View bills" onClick={() => navigate('./bills')} />
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	const Payment: React.FC = () => (
		<DashboardInfoDisplay>
			{/* <div>
					<InfoSmallText variant="field">Next payment</InfoSmallText>
					<InfoDisplayText>Dec 17, 2023</InfoDisplayText>
				</div> */}
			<div>
				<DashboardInfoSmallText variant="field">Default Card</DashboardInfoSmallText>
				<DashboardInfoDisplayText>
					Card ending in {currentPaymentInfo?.last4}{' '}
				</DashboardInfoDisplayText>
				<DashboardInfoDisplayText>
					Expires {currentPaymentInfo?.expMonth}/{currentPaymentInfo?.expYear}
				</DashboardInfoDisplayText>
			</div>
			<EditButtonWrapper>
				<Button
					disabled={false}
					label="Update payment method"
					onClick={() => navigate('./payment_edit')}
				/>
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	const InsuranceInfo = () => (
		<>
			{memberInsurance?.length ? (
				<DashboardInfoDisplay>
					<SpacedColumn gap="xssm">
						<DashboardInfoSmallText variant="field">Insurance coverage</DashboardInfoSmallText>
						<SpacedColumn gap="md">
							{memberInsurance.map((insurance: IMemberInsurance, index) => (
								<SpacedColumn key={index} gap="none">
									<DashboardInfoSmallText variant="field">
										{insurance.insuranceProviderName}
									</DashboardInfoSmallText>
									<DashboardInfoDisplayText>
										{maskInsurance(insurance.memberInsuranceId)}
									</DashboardInfoDisplayText>
								</SpacedColumn>
							))}
						</SpacedColumn>
					</SpacedColumn>
					<EditButtonWrapper>
						<Button
							disabled={false}
							label="Edit insurance"
							onClick={() => navigate('./insurance/coverage')}
						/>
					</EditButtonWrapper>
				</DashboardInfoDisplay>
			) : (
				<DashboardInfoDisplay>
					<div>
						<DashboardInfoSmallText variant="field">Insurance coverage</DashboardInfoSmallText>
						<DashboardInfoDisplayText>No insurance on record</DashboardInfoDisplayText>
					</div>
					<EditButtonWrapper>
						<Button
							disabled={false}
							label="Check if we're in your network"
							onClick={() => navigate('./insurance/provider')}
						/>
					</EditButtonWrapper>
				</DashboardInfoDisplay>
			)}
		</>
	);

	const Pharmacy = () => (
		<DashboardInfoDisplay>
			<div>
				<DashboardInfoSmallText variant="field">Delivery Method</DashboardInfoSmallText>
				<DashboardInfoDisplayText>
					{deliveryMethod ? `${pharmacyMap[deliveryMethod]}` : 'No pharmacy selected'}
				</DashboardInfoDisplayText>
			</div>
			<EditButtonWrapper>
				<Button label="Update pharmacy" onClick={() => navigate('./delivery_edit')} />
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	const PreferredPharmacy = () => (
		<>
			<DashboardInfoDisplay>
				<div>
					<DashboardInfoSmallText variant="field">Preferred Pharmacy</DashboardInfoSmallText>
					<DashboardInfoDisplayTextBold>
						{defaultPharmacy?.defaultPharmacy?.name ?? 'Pharmacy name'}
					</DashboardInfoDisplayTextBold>
					<DashboardInfoDisplayText>
						{defaultPharmacy?.defaultPharmacy?.address ?? 'Pharmacy address'}
					</DashboardInfoDisplayText>
				</div>
				<EditButtonWrapper>
					<Button label="Update pharmacy" onClick={() => navigate('./delivery_edit')} />
				</EditButtonWrapper>
			</DashboardInfoDisplay>
		</>
	);

	const Shipping = () => (
		<DashboardInfoDisplay>
			<FlexColumn gap="smmd">
				<div>
					<DashboardInfoSmallText variant="field">Default Address</DashboardInfoSmallText>
					<DashboardInfoDisplayText>{fullShippingName}</DashboardInfoDisplayText>
				</div>
				<div>
					<DashboardInfoDisplayText>{address?.line1 ? address.line1 : ''}</DashboardInfoDisplayText>
					{address?.line2 ? (
						<DashboardInfoDisplayText>{address.line2}</DashboardInfoDisplayText>
					) : (
						''
					)}
					<DashboardInfoDisplayText>
						{`${address?.city ? `${address?.city},` : ''} ${
							address?.state ?? member?.stateOfResidence?.abbreviation ?? ''
						} ${address?.zip ?? ''}`}
					</DashboardInfoDisplayText>
				</div>
			</FlexColumn>
			<EditButtonWrapper>
				<Button disabled={false} label="Edit" onClick={() => navigate('./shipping_edit')} />
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	return (
		<DashboardCardLayout>
			<ProfileDropdown />
			<HeadingText>Billing & Insurance</HeadingText>
			<FolxMembershipCard
				cardContent={folxMembership}
				b2bCustomer={billingPageInformation?.authenticatedMember.b2b}
				isMembershipWaivable={
					billingPageInformation?.authenticatedMember.isMembershipWaivable.isWaivable ?? false
				}
				invoices={billingPageInformation?.authenticatedMember.invoices}
				membershipSubscription={billingPageInformation?.authenticatedMember.membershipSubscription}
				protectedNavigate={protectedNavigate}
			/>
			<div>
				<SectionLabel>{pageContent.billsSectionHeading}</SectionLabel>
				<BaseCard bgColor="white" body={<Bills />} borderless radius="md" shadow />
			</div>
			{currentPaymentInfo ? (
				<>
					<div>
						<SectionLabel>Payment details</SectionLabel>
						<BaseCard bgColor="white" body={<Payment />} borderless radius="md" shadow />
					</div>
				</>
			) : null}
			<div>
				<SectionLabel>Insurance</SectionLabel>
				<BaseCard bgColor="white" body={<InsuranceInfo />} borderless radius="md" shadow />
			</div>
			<div>
				<SectionLabel>Pharmacy</SectionLabel>
				<BaseCard
					bgColor="white"
					body={isPreferredPharmacy ? <PreferredPharmacy /> : <Pharmacy />}
					borderless
					radius="md"
					shadow
				/>
			</div>
			<div>
				<SectionLabel>Shipping Address</SectionLabel>
				<BaseCard bgColor="white" body={<Shipping />} borderless radius="md" shadow />
			</div>
		</DashboardCardLayout>
	);
};

export default Billing;
